import type { PartnerTemplatePartner } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import {
  CATALOG_OTHER_DEPARTMENT,
  CATALOG_SOURCES,
  templateDepartmentMessages,
  templateGroupMessages,
  type CatalogFilter,
  type CatalogSource,
  type TemplateClassification,
} from '../config';

export type CatalogFilterable = {
  classification?: TemplateClassification | null;
  partner?: PartnerTemplatePartner;
  savedByClinic?: boolean;
};

const DEPARTMENT_IDS = Object.keys(templateDepartmentMessages);
const GROUP_IDS = Object.keys(templateGroupMessages);

const getDepartmentOf = (item: CatalogFilterable) => {
  const department = item.classification?.department;
  return department && DEPARTMENT_IDS.includes(department)
    ? department
    : CATALOG_OTHER_DEPARTMENT;
};

const getSourceOf = (item: CatalogFilterable): CatalogSource =>
  item.partner !== undefined
    ? 'partners'
    : item.savedByClinic
      ? 'saved'
      : 'ours';

// The departments the items fall into, in vocabulary order, then "Other".
export const getCatalogDepartments = (items: CatalogFilterable[]) => {
  const present = new Set(items.map(getDepartmentOf));
  return [...DEPARTMENT_IDS, CATALOG_OTHER_DEPARTMENT].filter((id) =>
    present.has(id),
  );
};

// The groups of one department the items fall into, in vocabulary order.
export const getCatalogGroups = (
  items: CatalogFilterable[],
  department: string,
) =>
  GROUP_IDS.filter(
    (group) =>
      group.startsWith(`${department}.`) &&
      items.some((item) => item.classification?.group === group),
  );

export const getCatalogSources = (items: CatalogFilterable[]) =>
  CATALOG_SOURCES.filter((source) =>
    items.some((item) => getSourceOf(item) === source),
  );

export const filterCatalogItems = <T extends CatalogFilterable>(
  items: T[],
  { source, department, group }: CatalogFilter,
) =>
  items.filter(
    (item) =>
      (!source || getSourceOf(item) === source) &&
      (!department || getDepartmentOf(item) === department) &&
      (!group || item.classification?.group === group),
  );

// The filter as it applies: with items of one source the switch is hidden,
// and its default must not hide them.
export const resolveCatalogFilter = (
  items: CatalogFilterable[],
  filter: CatalogFilter,
): CatalogFilter => ({
  ...filter,
  source: getCatalogSources(items).length > 1 ? filter.source : null,
});
